/**
 * Multi-Team Data Retrieval Service
 * Implements nested iteration: teams → managers → activity data
 * Handles the complex logic of fetching comprehensive team data using service account
 */

import { 
  CrossoverTeam, 
  CrossoverTeamsResponse, 
  TeamMemberActivityWithContext,
  MultiTeamDataResult,
  TeamActivityRequest
} from '../types/teamTypes';
import { 
  CrossoverTeamActivityResponse,
  CrossoverTeamMemberActivity 
} from '../types';
import { formatTimestampToDate, getMondayMidnightUTC } from './dailyUsageApiService';
import { getApiEndpoint } from '../config/amplifyConfig';

// Base URLs for API endpoints
const TEAMS_API_BASE = 'https://api.crossover.com/api/internal/teams/dashboard';
const ACTIVITY_API_BASE = 'https://api.crossover.com/api/tracker/activity/groups';

/**
 * Fetch teams accessible to a manager using service account
 * @param managerId - Manager ID to fetch teams for
 * @returns Promise<CrossoverTeam[]> - Array of accessible teams
 */
export const fetchAccessibleTeams = async (managerId: number): Promise<CrossoverTeam[]> => {
  console.log('🏢 [MULTI-TEAM] Fetching teams for managerId:', managerId);
  
  try {
    // Call our Lambda endpoint for teams discovery
    const endpoint = getApiEndpoint('/crossover/teams');
    console.log('🔗 [MULTI-TEAM] Using endpoint:', endpoint);
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ managerId: managerId.toString() }),
    });

    if (!response.ok) {
      throw new Error(`Teams API failed: ${response.status} ${response.statusText}`);
    }

    const teamsData: CrossoverTeamsResponse = await response.json();
    
    console.log('✅ [MULTI-TEAM] Teams fetched successfully:', {
      teamsCount: teamsData.teams.length,
      teams: teamsData.teams.map(t => ({ id: t.id, name: t.name }))
    });

    return teamsData.teams;
  } catch (error) {
    console.error('🚨 [MULTI-TEAM] Error fetching teams:', error);
    throw new Error(`Failed to fetch teams: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Fetch Central PS teams using service account (for permission checking)
 * Returns all teams accessible to service account
 * @returns Promise<CrossoverTeam[]> - Array of all Central PS teams
 */
export const fetchCentralPSTeams = async (): Promise<CrossoverTeam[]> => {
  console.log('🏢 [CENTRAL-PS] Fetching Central PS teams for permission check');
  
  try {
    const endpoint = getApiEndpoint('/crossover/central-ps-teams');
    console.log('🔗 [CENTRAL-PS] Using endpoint:', endpoint);
    
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Central PS teams API failed: ${response.status} ${response.statusText}`);
    }

    const data: CrossoverTeamsResponse = await response.json();
    
    console.log('✅ [CENTRAL-PS] Central PS teams fetched successfully:', {
      teamsCount: data.teams.length,
      teams: data.teams.map(t => ({ 
        id: t.id, 
        name: t.name, 
        teamOwnerId: t.teamOwner.id 
      }))
    });

    return data.teams;
  } catch (error) {
    console.error('🚨 [CENTRAL-PS] Error fetching Central PS teams:', error);
    throw new Error(`Failed to fetch Central PS teams: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Fetch Central PS teams with assignments to extract userIds
 * This is faster than comprehensive data for Worksmart AI filtering
 * @returns Promise<{teams: CrossoverTeam[], assignments: any[]}> - Teams and assignments with userIds
 */
export const fetchCentralPSTeamsWithAssignments = async (): Promise<{teams: CrossoverTeam[], assignments: any[]}> => {
  console.log('🏢 [CENTRAL-PS] Fetching Central PS teams with assignments for userId extraction');
  
  try {
    const endpoint = getApiEndpoint('/crossover/central-ps-teams');
    console.log('🔗 [CENTRAL-PS] Using endpoint:', endpoint);
    
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Central PS teams API failed: ${response.status} ${response.statusText}`);
    }

    const data: any = await response.json();
    
    // Extract assignments if available (backend may return assignments array)
    const assignments = data.assignments || [];
    
    console.log('✅ [CENTRAL-PS] Central PS teams with assignments fetched successfully:', {
      teamsCount: data.teams?.length || 0,
      assignmentsCount: assignments.length
    });

    return {
      teams: data.teams || [],
      assignments: assignments
    };
  } catch (error) {
    console.error('🚨 [CENTRAL-PS] Error fetching Central PS teams with assignments:', error);
    throw new Error(`Failed to fetch Central PS teams with assignments: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Extract userIds from Central PS assignments
 * Used for Worksmart AI filtering - much faster than comprehensive data
 * @returns Promise<number[]> - Array of userIds from Central PS team assignments
 */
export const extractCentralPSUserIds = async (): Promise<number[]> => {
  console.log('📊 [WORKSMART-AI] Extracting Central PS user IDs from teams/assignments');
  
  try {
    const { assignments } = await fetchCentralPSTeamsWithAssignments();
    
    const userIds = new Set<number>();
    
    assignments.forEach((assignment: any) => {
      // Extract userId from assignment (matches Worksmart xo.user_id)
      const userId = assignment.selection?.marketplaceMember?.application?.candidate?.userId;
      
      if (userId && userId !== 0) {
        userIds.add(userId);
      }
    });
    
    const userIdArray = Array.from(userIds);
    
    console.log('✅ [WORKSMART-AI] Extracted Central PS user IDs:', {
      count: userIdArray.length,
      sampleUserIds: userIdArray.slice(0, 10)
    });
    
    return userIdArray;
  } catch (error) {
    console.error('🚨 [WORKSMART-AI] Error extracting Central PS user IDs:', error);
    throw new Error(`Failed to extract Central PS user IDs: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Fetch activity data for a specific team and manager combination
 * @param teamId - Team ID
 * @param managerId - Manager ID  
 * @param weekDate - Week date (any day, will be converted to Monday)
 * @returns Promise<CrossoverTeamActivityResponse> - Team activity data
 */
export const fetchTeamManagerActivity = async (
  teamId: number,
  managerId: number, 
  weekDate: string
): Promise<CrossoverTeamActivityResponse> => {
  
  console.log('📊 [MULTI-TEAM] Fetching activity for team-manager combination:', {
    teamId,
    managerId,
    weekDate
  });

  try {
    // Convert to Monday date format expected by API
    const mondayTimestamp = getMondayMidnightUTC(weekDate);
    const mondayDate = formatTimestampToDate(mondayTimestamp);
    
    // Call our Lambda endpoint for team activity using service account
    const endpoint = getApiEndpoint('/crossover/team-activity');
    console.log('🔗 [MULTI-TEAM] Using team activity endpoint:', endpoint);
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        teamId,
        managerId,
        date: mondayDate,
        weekly: true
      }),
    });

    if (!response.ok) {
      if (response.status === 404) {
        console.log('⚠️ [MULTI-TEAM] No activity data found for team-manager combination');
        return []; // Return empty array for no data
      }
      throw new Error(`Activity API failed: ${response.status} ${response.statusText}`);
    }

    const activityData: CrossoverTeamActivityResponse = await response.json();
    
    console.log('✅ [MULTI-TEAM] Activity data fetched:', {
      teamId,
      managerId,
      memberCount: activityData.length
    });

    return activityData;
  } catch (error) {
    console.error('🚨 [MULTI-TEAM] Error fetching team activity:', {
      teamId,
      managerId,
      error: error instanceof Error ? error.message : 'Unknown error'
    });
    
    // Don't throw for individual team failures - just return empty data
    console.log('⚠️ [MULTI-TEAM] Returning empty data for failed team-manager combination');
    return [];
  }
};

/**
 * Add team context to activity data
 * @param activities - Raw activity data
 * @param team - Team information
 * @param managerId - Manager ID who provided the data
 * @param managerName - Manager name
 * @returns TeamMemberActivityWithContext[] - Activity data with team context
 */
export const addTeamContextToActivities = (
  activities: CrossoverTeamActivityResponse,
  team: CrossoverTeam,
  managerId: number,
  managerName: string
): TeamMemberActivityWithContext[] => {
  
  return activities.map(activity => ({
    ...activity,
    teamId: team.id,
    teamName: team.name,
    managerId,
    managerName
  }));
};

/**
 * Fetch comprehensive multi-team data using nested iteration
 * @param requestingManagerId - Manager ID making the request (for permissions)
 * @param weekDate - Week to fetch data for
 * @returns Promise<MultiTeamDataResult> - Complete multi-team dataset
 */
export const fetchMultiTeamData = async (
  requestingManagerId: number,
  weekDate: string
): Promise<MultiTeamDataResult> => {
  
  console.log('🚀 [MULTI-TEAM] === Starting Multi-Team Data Fetch ===');
  console.log('🔍 [MULTI-TEAM] Parameters:', {
    requestingManagerId,
    weekDate
  });

  const startTime = Date.now();
  
  try {
    // Step 1: Fetch all accessible teams
    console.log('📋 [MULTI-TEAM] Step 1: Fetching accessible teams...');
    const teams = await fetchAccessibleTeams(requestingManagerId);
    
    if (teams.length === 0) {
      console.log('⚠️ [MULTI-TEAM] No teams found for manager');
      return {
        teams: [],
        activities: [],
        summary: {
          totalTeams: 0,
          totalManagers: 0,
          totalTeamMembers: 0,
          requestedManagerId: requestingManagerId.toString(),
          dateRequested: weekDate,
          timestamp: new Date().toISOString()
        }
      };
    }

    // Step 2: Build list of all (team, manager) combinations
    console.log('🔄 [MULTI-TEAM] Step 2: Building team-manager combinations...');
    const teamManagerCombinations: Array<{
      team: CrossoverTeam;
      managerId: number;
      managerName: string;
    }> = [];

    teams.forEach(team => {
      // Add team owner
      teamManagerCombinations.push({
        team,
        managerId: team.teamOwner.id,
        managerName: team.teamOwner.printableName
      });

      // Add all reporting managers
      team.reportingManagers.forEach(manager => {
        teamManagerCombinations.push({
          team,
          managerId: manager.id,
          managerName: manager.printableName
        });
      });
    });

    console.log('📊 [MULTI-TEAM] Team-manager combinations to process:', {
      totalCombinations: teamManagerCombinations.length,
      teamsCount: teams.length,
      combinationsBreakdown: teams.map(team => ({
        teamName: team.name,
        combinationsForThisTeam: 1 + team.reportingManagers.length
      }))
    });

    // Step 3: Fetch activity data for all combinations (with parallel processing)
    console.log('⏳ [MULTI-TEAM] Step 3: Fetching activity data for all combinations...');
    
    const activityPromises = teamManagerCombinations.map(({ team, managerId, managerName }) =>
      fetchTeamManagerActivity(team.id, managerId, weekDate)
        .then(activities => ({
          team,
          managerId,
          managerName,
          activities: addTeamContextToActivities(activities, team, managerId, managerName)
        }))
        .catch(error => {
          console.error(`🚨 [MULTI-TEAM] Failed to fetch activity for team ${team.name} (${team.id}) with manager ${managerName} (${managerId}):`, error);
          return {
            team,
            managerId,
            managerName,
            activities: [] as TeamMemberActivityWithContext[]
          };
        })
    );

    const activityResults = await Promise.all(activityPromises);
    
    // Step 4: Aggregate all activities
    console.log('🔗 [MULTI-TEAM] Step 4: Aggregating activity data...');
    const allActivities: TeamMemberActivityWithContext[] = [];
    
    activityResults.forEach(result => {
      allActivities.push(...result.activities);
    });

    // Remove duplicates (same user might appear in multiple teams)
    const uniqueActivities = removeDuplicateActivities(allActivities);
    
    // Step 5: Create summary
    const uniqueManagers = new Set(teamManagerCombinations.map(c => c.managerId));
    const endTime = Date.now();
    
    const result: MultiTeamDataResult = {
      teams,
      activities: uniqueActivities,
      summary: {
        totalTeams: teams.length,
        totalManagers: uniqueManagers.size,
        totalTeamMembers: uniqueActivities.length,
        requestedManagerId: requestingManagerId.toString(),
        dateRequested: weekDate,
        timestamp: new Date().toISOString()
      }
    };

    console.log('✅ [MULTI-TEAM] === Multi-Team Data Fetch Complete ===');
    console.log('📈 [MULTI-TEAM] Final results:', {
      ...result.summary,
      processingTimeMs: endTime - startTime,
      combinationsProcessed: teamManagerCombinations.length,
      successfulFetches: activityResults.filter(r => r.activities.length > 0).length
    });

    return result;

  } catch (error) {
    console.error('🚨 [MULTI-TEAM] === Multi-Team Data Fetch Failed ===');
    console.error('🚨 [MULTI-TEAM] Error:', error);
    throw new Error(`Multi-team data fetch failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Remove duplicate team member activities (same user appearing in multiple teams)
 * Keeps the activity with the most complete data
 * @param activities - Array of activities with potential duplicates
 * @returns Deduplicated array of activities
 */
const removeDuplicateActivities = (activities: TeamMemberActivityWithContext[]): TeamMemberActivityWithContext[] => {
  const userMap = new Map<number, TeamMemberActivityWithContext>();
  
  activities.forEach(activity => {
    const userId = activity.assignment?.candidate?.id;
    if (!userId) return;
    
    const existing = userMap.get(userId);
    if (!existing) {
      userMap.set(userId, activity);
    } else {
      // For CentralPS with multiple reporting managers: Merge data instead of replacing
      // Each manager may see different/additional data for the same user
      const existingTime = existing.grouping?.totalTrackedTime || 0;
      const currentTime = activity.grouping?.totalTrackedTime || 0;
      
      // Use the activity with more complete data (higher tracked time)
      // But preserve both activities' contexts for debugging
      if (currentTime > existingTime) {
        // Update to current activity but preserve reporting manager context
        const mergedActivity = {
          ...activity,
          // Add debug info about which managers reported this user
          __debugManagers: [
            ...((existing as any).__debugManagers || [existing.managerName]), 
            activity.managerName
          ].filter((name, index, arr) => arr.indexOf(name) === index)
        } as TeamMemberActivityWithContext;
        userMap.set(userId, mergedActivity);
      } else {
        // Keep existing but update debug info
        const mergedActivity = {
          ...existing,
          __debugManagers: [
            ...((existing as any).__debugManagers || [existing.managerName]), 
            activity.managerName
          ].filter((name, index, arr) => arr.indexOf(name) === index)
        } as TeamMemberActivityWithContext;
        userMap.set(userId, mergedActivity);
      }
    }
  });
  
  const uniqueActivities = Array.from(userMap.values());
  
  console.log('🔍 [MULTI-TEAM] Enhanced duplicate removal for CentralPS:', {
    originalCount: activities.length,
    uniqueCount: uniqueActivities.length,
    duplicatesRemoved: activities.length - uniqueActivities.length,
    usersWithMultipleManagers: uniqueActivities.filter(a => (a as any).__debugManagers?.length > 1).length
  });
  
  return uniqueActivities;
};

/**
 * Fetch comprehensive data from the new Lambda endpoint
 * Service account gets its own managerId from profile API and fetches all accessible teams  
 * Frontend handles filtering based on logged-in user's managerId for Team/Global views
 * @param weekDate - Week date for activity data (YYYY-MM-DD format)
 * @returns Promise<MultiTeamDataResult> - Comprehensive team and activity data
 */
export const fetchComprehensiveData = async (weekDate: string): Promise<MultiTeamDataResult> => {
  console.log('🌍 [COMPREHENSIVE] Fetching comprehensive data:', { weekDate });
  
  try {
    const endpoint = getApiEndpoint('/crossover/comprehensive-data');
    console.log('🔗 [COMPREHENSIVE] Using endpoint:', endpoint);
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        weekDate 
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('🚨 [COMPREHENSIVE] API error:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText
      });
      throw new Error(`Comprehensive data API failed: ${response.status} ${response.statusText}`);
    }

    const data: MultiTeamDataResult = await response.json();
    
    console.log('✅ [COMPREHENSIVE] Data fetched successfully:', {
      teamsCount: data.teams.length,
      activitiesCount: data.activities.length,
      summary: data.summary
    });

    return data;
  } catch (error) {
    console.error('🚨 [COMPREHENSIVE] Error fetching comprehensive data:', error);
    throw new Error(`Failed to fetch comprehensive data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}; 

/**
 * Fetch team data for non-manager team members
 * Uses service account via backend to fetch team data with security verification
 * @param teamId - Team ID
 * @param teamOwnerManagerId - Manager ID of the team owner
 * @param weekDate - Monday date in YYYY-MM-DD format
 * @param userToken - User's authentication token (for authorization only)
 * @returns Promise<MultiTeamDataResult> - Team activity data
 */
export const fetchNonManagerTeamData = async (
  teamId: number,
  teamOwnerManagerId: number,
  weekDate: string,
  userToken: string
): Promise<MultiTeamDataResult> => {
  console.log('📡 [NON-MANAGER-TEAM] Fetching team data for non-manager:', {
    teamId,
    teamOwnerManagerId,
    weekDate
  });
  
  try {
    // Call Lambda with teamId and teamOwnerManagerId parameters
    // Backend will:
    // 1. Authenticate user via token
    // 2. Verify user is member of this team (security check)
    // 3. Use SERVICE ACCOUNT to fetch team activity data
    // 4. Return team data in same structure as manager team data
    
    const endpoint = getApiEndpoint('/crossover/manager-team-data');
    console.log('🔗 [NON-MANAGER-TEAM] Using endpoint:', endpoint);
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Auth-Token': userToken // For authentication/authorization only
      },
      body: JSON.stringify({
        teamId,
        teamOwnerManagerId,
        weekDate,
        userToken
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('🚨 [NON-MANAGER-TEAM] Failed to fetch team data:', {
        status: response.status,
        error: errorText
      });
      throw new Error(`Failed to fetch non-manager team data: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    console.log('✅ [NON-MANAGER-TEAM] Team data fetched successfully:', {
      activitiesCount: data.activities?.length || 0
    });
    
    return data;
  } catch (error: any) {
    console.error('🚨 [NON-MANAGER-TEAM] Error fetching non-manager team data:', error);
    throw error;
  }
};

/**
 * Fetch team data for a specific manager using their personal authentication token
 * This allows managers outside Central PS to access their team data
 * @param managerId - Manager ID to fetch team data for
 * @param weekDate - Week date for activity data (YYYY-MM-DD format)
 * @param userToken - User's X-Auth-Token for authentication
 * @returns Promise<MultiTeamDataResult> - Manager's team data
 */
export const fetchManagerTeamData = async (
  managerId: number,
  weekDate: string,
  userToken: string
): Promise<MultiTeamDataResult> => {
  console.log('👤 [MANAGER-TEAM] Fetching manager team data:', { managerId, weekDate });
  
  try {
    const endpoint = getApiEndpoint('/crossover/manager-team-data');
    console.log('🔗 [MANAGER-TEAM] Using endpoint:', endpoint);
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        managerId,
        weekDate,
        userToken
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('🚨 [MANAGER-TEAM] API error:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText
      });
      throw new Error(`Manager team data API failed: ${response.status} ${response.statusText}`);
    }

    const data: MultiTeamDataResult = await response.json();
    
    console.log('✅ [MANAGER-TEAM] Data fetched successfully:', {
      teamsCount: data.teams.length,
      activitiesCount: data.activities.length,
      summary: data.summary
    });

    return data;
  } catch (error) {
    console.error('🚨 [MANAGER-TEAM] Error fetching manager team data:', error);
    throw new Error(`Failed to fetch manager team data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}; 